import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import { NavigateOptions, useSearchParams } from 'react-router-dom';

import { startTimeDesc } from 'components/Search/const';

import { useSavedSearch } from 'components/SavedSearch/SavedSearchContextProvider';
import { ApiErrorResponse, BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { buildSavedSearchParams } from 'components/Search/functions';
import {
  getQuickFilterIdQp,
  normalizeQpStr,
  QUICKFILTER_ID_QP,
  SavedSearchSelector,
} from 'components/SavedSearch/SavedSearchSelector';
import {
  GetSavedSearchByIdResult,
  SavedSearchItem,
} from 'providers/contexts/extensions/SavedSearches';
import { useSearch } from 'components/Search/SearchContextProvider';
import { convertSavedSearchToModalFilters } from './utils/editorialSavedSearchUtils';

// keep quickfilter value if just sort/dir changes and ignore loader param
const keysIgnore = ['sort', 'dir', QUICKFILTER_ID_QP];

export const EditorialQuickFilterSelector = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const {
    setSavedSearchOpen,
    quickFilterValue,
    updateQuickFilterValue,
    lastQuickFilterRef,
  } = useSavedSearch();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get search context to detect modal mode and access updateQueryParams
  const searchContext = useSearch();
  const isModalSearch = searchContext?.isModalSearch || false;
  const updateQueryParams = searchContext?.updateQueryParams;

  // extract possible quick filter fetch id from the qp
  const maybeQuickFilterId = getQuickFilterIdQp(searchParams);

  const {
    data: getIdData,
    isPending: getIdIsPending,
    isError: getIdIsError,
    isSuccess: getIdIsSuccess,
  } = useQuery<GetSavedSearchByIdResult, ApiErrorResponse, GetSavedSearchByIdResult>({
    queryKey: ['getSavedSearchById', maybeQuickFilterId],
    queryFn: async () => {
      // React Query v5 requires queryFn to always return a defined value (not undefined),
      // even for async functions. The 'enabled' option ensures this function only runs
      // when maybeQuickFilterId is truthy, so we can safely call the dataProvider
      // without needing an early return for falsy IDs.
      const result = await dataProvider.getSavedSearchById({
        path: { id: maybeQuickFilterId! },
      });
      return result;
    },
    enabled: Boolean(maybeQuickFilterId),
    gcTime: 0, // no auto bg refetch / no cache
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Disabled and loading states for selector btn
  // In React Query v5, isPending is true for disabled queries
  // Only show loading when query is enabled AND pending
  const isDisabled = Boolean(maybeQuickFilterId) && getIdIsPending;
  const isLoading = Boolean(maybeQuickFilterId) && getIdIsPending;

  // track last pending/applied params
  const pendingChangeRef = useRef(false);

  // handle applying the filter
  const applyFilter = useCallback(
    (item: SavedSearchItem, navOpts: NavigateOptions) => {
      // build editorial saved search filter payload
      const ssPayload = item?.payload || {};

      if (isModalSearch && updateQueryParams) {
        // Modal mode: Apply filters directly to local state via context
        // Convert payload to proper filter format with key mappings
        const filters = convertSavedSearchToModalFilters(ssPayload, {
          sort: 'start_time',
          dir: 'DESC',
        });

        // Apply filters to local state
        updateQueryParams(filters);
        updateQuickFilterValue(item);
      } else {
        // URL mode: Use setSearchParams for navigation
        const newParamStr = buildSavedSearchParams({
          payload: ssPayload,
          addParams: {
            ...startTimeDesc,
          },
        });

        // if empty do not apply
        if (!newParamStr) {
          return;
        }
        // apply to select state, url, and track pending/applied via refs
        pendingChangeRef.current = true;
        updateQuickFilterValue(item);
        setSearchParams(newParamStr, navOpts);
      }
    },
    [isModalSearch, updateQueryParams, setSearchParams, updateQuickFilterValue],
  );

  useEffect(() => {
    // Skip quickFilterId URL param logic in modal mode
    if (isModalSearch) {
      return;
    }

    if (!maybeQuickFilterId) {
      return;
    }
    if (getIdIsPending) {
      return;
    }

    // upon fetched set the value and remove param
    // apply the filter to both state and URL, replace history stack
    // as we are removing the quickFilterId param as well...
    if (getIdIsSuccess && getIdData?.data) {
      applyFilter(getIdData.data, { replace: true });
    }

    // If the fetch errored, reset the filter value
    // and just remove the quickFilterId param
    if (getIdIsError) {
      updateQuickFilterValue(null);

      // remove just the quickFilterId param as fetch failed
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams.toString());
          newParams.delete(QUICKFILTER_ID_QP);
          return newParams;
        },
        { replace: true },
      );
    }
  }, [
    isModalSearch,
    applyFilter,
    getIdData?.data,
    getIdIsPending,
    getIdIsSuccess,
    getIdIsError,
    maybeQuickFilterId,
    setSearchParams,
    updateQuickFilterValue,
  ]);

  // add in client side items to the selector, in this case
  // adding to the list the selected filter value, and
  // selector internally should deduplicate items by id anyway
  const addItems = useMemo(() => {
    return quickFilterValue ? [quickFilterValue] : [];
  }, [quickFilterValue]);

  // dispatch filter apply on item select
  const onFilterSelect = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, item: SavedSearchItem) => {
      applyFilter(item, { replace: false });
    },
    [applyFilter],
  );

  // reset quick filter value if the query params change and
  // does not match the last applied params as normalized
  useEffect(() => {
    // Skip URL-based comparison in modal mode
    // The modal filters are in state and don't read from URL
    if (isModalSearch) {
      return;
    }

    // prevent instant reset upon filter selection
    if (pendingChangeRef.current) {
      pendingChangeRef.current = false;
      return;
    }

    // no value or no last applied params ref
    if (!quickFilterValue || !lastQuickFilterRef.current) {
      return;
    }

    // compare current vs last applied params
    const normalizedCurrent = normalizeQpStr({
      qpstr: searchParams.toString(),
      ignoreKeys: keysIgnore,
    });

    const lastQpStr = buildSavedSearchParams({
      payload: lastQuickFilterRef?.current?.payload ?? {},
    });

    const normalizedLast = normalizeQpStr({
      qpstr: lastQpStr,
      ignoreKeys: keysIgnore,
    });

    // mismatch indicates params changed so
    // reset value as that filter may not be applied anymore
    if (normalizedCurrent !== normalizedLast) {
      updateQuickFilterValue(null);
    }
  }, [
    isModalSearch,
    lastQuickFilterRef,
    quickFilterValue,
    searchParams,
    updateQuickFilterValue,
  ]);

  // open saved search modal on new click
  const createNewSavedSearch = useCallback(() => {
    setSavedSearchOpen(true);
  }, [setSavedSearchOpen]);

  return (
    <SavedSearchSelector
      value={quickFilterValue}
      clientItems={addItems}
      disabled={isDisabled}
      loading={isLoading}
      onSelectCb={onFilterSelect}
      createNewCb={createNewSavedSearch}
      addPayload={{
        query: {
          app: ['editorial'],
        },
      }}
    />
  );
};
EditorialQuickFilterSelector.displayName = 'EditorialQuickFilterSelector';
